'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { cn } from '@morpho-org/ui-v6/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { capitalize } from 'lodash-es';
import { type ForwardedRef, forwardRef } from 'react';
import { Avatar, type AvatarSize, getAvatarSize } from './Avatar';
import { NetworkAvatar } from './NetworkAvatar';
export interface NetworkOffset {
  right: number;
  bottom: number;
}
interface AvatarMultichainProps {
  src: string | null;
  chain: {
    id: ChainId;
    name: string;
  } | null;
  size?: AvatarSize;
  networkSize?: number;
  networkOffset?: NetworkOffset;
  padding?: number;
  className?: string;
}
const getNetworkSize = (size?: AvatarSize): number => {
  switch (size) {
    case 'xsmall':
      return 8;
    case 'small':
      return 10;
    case 'medium':
      return 12;
    case 'regular':
      return 18;
    case 'big':
      return 28;
    case 'large':
      return 32;
    default:
      return typeof size === 'number' ? Math.round(size * 0.4) : 10;
  }
};
const AvatarMultichain = (t0, ref) => {
  const $ = _c(29);
  const {
    src,
    chain,
    size: t1,
    networkSize,
    networkOffset: t2,
    padding,
    className
  } = t0;
  const size = t1 === undefined ? "small" : t1;
  let t3;
  if ($[0] !== t2) {
    t3 = t2 === undefined ? {
      right: 0,
      bottom: 0
    } : t2;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const networkOffset = t3;
  let t4;
  if ($[2] !== size) {
    t4 = getAvatarSize(size);
    $[2] = size;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const dimension = t4;
  let t5;
  if ($[4] !== networkSize || $[5] !== size) {
    t5 = networkSize ?? getNetworkSize(size);
    $[4] = networkSize;
    $[5] = size;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const computedNetworkSize = t5;
  let t6;
  if ($[7] !== networkOffset.bottom || $[8] !== networkOffset.right) {
    t6 = {
      right: networkOffset.right,
      bottom: networkOffset.bottom
    };
    $[7] = networkOffset.bottom;
    $[8] = networkOffset.right;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const networkPositionStyle = t6;
  let t7;
  if ($[10] !== dimension || $[11] !== size) {
    t7 = typeof size === "number" ? {
      width: dimension,
      height: dimension
    } : undefined;
    $[10] = dimension;
    $[11] = size;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const containerStyle = t7;
  let t8;
  if ($[13] !== className) {
    t8 = cn("relative inline-flex aspect-square shrink-0 items-center justify-center", className);
    $[13] = className;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== padding || $[16] !== size || $[17] !== src) {
    t9 = <Avatar src={src} size={size} padding={padding} />;
    $[15] = padding;
    $[16] = size;
    $[17] = src;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== chain || $[20] !== computedNetworkSize || $[21] !== networkPositionStyle) {
    t10 = chain && <Tooltip><TooltipTrigger asChild={true}><div className="absolute flex" style={networkPositionStyle}><NetworkAvatar chainId={chain.id} srcAvatarProps={{
            size: computedNetworkSize,
            padding: 0
          }} /></div></TooltipTrigger><TooltipContent>{capitalize(chain.name)}</TooltipContent></Tooltip>;
    $[19] = chain;
    $[20] = computedNetworkSize;
    $[21] = networkPositionStyle;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== containerStyle || $[24] !== ref || $[25] !== t10 || $[26] !== t8 || $[27] !== t9) {
    t11 = <div ref={ref} className={t8} style={containerStyle}>{t9}{t10}</div>;
    $[23] = containerStyle;
    $[24] = ref;
    $[25] = t10;
    $[26] = t8;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
};
const ForwardedAvatarMultichain = forwardRef(AvatarMultichain);
ForwardedAvatarMultichain.displayName = 'AvatarMultichain';
export { ForwardedAvatarMultichain as AvatarMultichain };